import { inject } from 'vue'
import type { MapManager } from '@onxmaps/map-x'
import type { StylesheetProvider } from '@onxmaps/map-x/stylesheet'
import { WEBMAP_MAP_MANAGER_KEY } from '@/services/map-provider/injection-key'
import { getMapManager } from '@/services/map-provider/map-manager-state'
import type { WebmapMapViewer } from '@/lib/map-x/webmap-viewer-types'
import { warn } from '@/lib/logger'

/**
 * MapManager with webmap-specific viewer type
 * Used internally by useMapManager composable
 * exported for typing MapManager arguments in map-specific utilities
 */
export type WebmapMapManager = MapManager<{
  viewer: WebmapMapViewer
  stylesheet: StylesheetProvider
}>

/**
 * Use the MapManager
 *
 * Returns MapManager with viewer typed as WebmapMapViewer (union of all webmap viewer implementations).
 * First attempts Vue injection, then falls back to direct instance access.
 *
 * @param key - Optional symbol key for multi-map support (defaults to primary key)
 * @returns a MapManager instance from the app-level provider
 * @throws {Error} if MapManager is not available via injection or direct access
 */
export function useMapManager(
  key: symbol = WEBMAP_MAP_MANAGER_KEY
): WebmapMapManager {
  // Try injection first (works in component setup)
  const mapManager = inject<WebmapMapManager | undefined>(key)
  if (mapManager) {
    // Type assertion with unknown intermediate for intentional narrowing
    // Cast is safe as long as the provider-created MapManager was created with a WebmapMapViewer
    return mapManager
  }

  // Fall back to direct instance access (works in watchers, lifecycle hooks, utilities, outside of Vue context)
  const directManager = getMapManager(key)
  if (directManager) {
    warn(
      'Map',
      'useMapManager(): MapManager not found via injection, using direct instance access as fallback. Ensure provideMapManager() has been called.'
    )
    return directManager as unknown as WebmapMapManager
  }

  throw new Error(
    `MapManager not available - provideMapManager() has not been called yet for key: ${key.toString()}`
  )
}
